import { GetDesignsByDesignValidateApiResponse, ProductVariantResource } from '@/api/serverApi'
import ProductConfigurationTable from './product-configuration-table'
import TBoltOptionType from './product-configuration-table/types/TBoltOptionType'
import { useMemo } from 'react'
import { FilledCheckIcon, FilledCrossIcon } from '@cfs-fixings/shared'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/stores'
import { setSelectedProductConfigurationOption } from '@/stores/product-picker'

const utilisationMapping = {
  tension: [
    'tension_anchor_failure',
    'tension_anchor_channel_connection_failure',
    'tension_local_flexure_of_channel_lips_failure',
    'tension_channel_bolt_failure',
    'tension_flexure_of_channel_failure',
    'tension_pull_out_failure',
    'tension_splitting_failure',
    'tension_blowout_failure',
  ],
  shear: [
    'shear_channel_bolt_failure',
    'shear_anchor_failure',
    'shear_anchor_channel_connection_failure',
    'shear_local_flexure_of_channel_lips_failure',
    'shear_pry_out_failure',
    'shear_concrete_edge_failure',
  ],
  combined: [
    'combined_t_bolt_failure',
    'combined_channel_lips_and_flexural_failure_of_channel',
    'combined_anchor_and_connection_between_anchor_and_channel_failure',
    'combined_concrete_failure',
  ],
}

type PropTypes = {
  parent: ProductVariantResource
  options: TBoltOptionType[]
  validationResult?: GetDesignsByDesignValidateApiResponse
}

type TBoltRowPropTypes = {
  parent: ProductVariantResource
  option: TBoltOptionType
  validationResult?: GetDesignsByDesignValidateApiResponse
}

const TBoltConfigurationHeader = () => {
  return (
    <ProductConfigurationTable.HeaderRow>
      <ProductConfigurationTable.HeaderCell> </ProductConfigurationTable.HeaderCell>
      <ProductConfigurationTable.HeaderCell>
        Bolt
      </ProductConfigurationTable.HeaderCell>
      <ProductConfigurationTable.HeaderCell>
        <div className="flex flex-row justify-end">
          Bolt Diameter
        </div>
      </ProductConfigurationTable.HeaderCell>
      <ProductConfigurationTable.HeaderCell>
        <div className="flex flex-row justify-end">
          Tension Evaluation
        </div>
      </ProductConfigurationTable.HeaderCell>
      <ProductConfigurationTable.HeaderCell>
        <div className="flex flex-row justify-end">
          Shear Evaluation
        </div>
      </ProductConfigurationTable.HeaderCell>
      <ProductConfigurationTable.HeaderCell>
        <div className="flex flex-row justify-end">
          Combined Evaluation
        </div>
      </ProductConfigurationTable.HeaderCell>
      <ProductConfigurationTable.HeaderCell>
        <div className="flex flex-row justify-end">
          Embedment Depth of Channel
        </div>
      </ProductConfigurationTable.HeaderCell>
      <ProductConfigurationTable.HeaderCell>
        <div className="flex flex-row justify-end">
          Pass / Fail
        </div>
      </ProductConfigurationTable.HeaderCell>
    </ProductConfigurationTable.HeaderRow>
  )
}

const TBoltConfigurationBody = ({ parent, options, validationResult }: PropTypes) => {
  return (
    <ProductConfigurationTable.Body>
      {options.map((option) => (
        <TBoltConfigurationRow
          key={`${option.productVariant.id}-${option.diameter.name}`}
          parent={parent}
          option={option}
          validationResult={validationResult}
        />
      ))}
    </ProductConfigurationTable.Body>
  )
}

const TBoltConfigurationRow = ({ parent, option, validationResult }: TBoltRowPropTypes) => {
  const dispatch = useDispatch()

  const selectedConfiguration = useSelector((state: RootState) => state.productPickerSlice.selectedProductConfigurationOption)

  const tensionPercentage = useMemo(() => {
    const result = validationResult?.data.alternative_configurations.find((item) => item.variant.id === option.productVariant.id && item.t_bolt?.diameter.name === option.diameter.name)

    if (!result) {
      return null
    }

    const value = utilisationMapping.tension.reduce((acc: number | null, item: string) => {
      const record = result.validation_result.find((dataItem) => dataItem.id === item)

      const utilisation = record?.utilisation ? parseFloat(record?.utilisation) : null

      if (record?.error && !utilisation) {
        return null
      }

      if (utilisation! > (acc ?? 0)) {
        return utilisation
      } else {
        return acc
      }
    }, null)

    return value ? value * 100 : null
  }, [option, validationResult])

  const shearPercentage = useMemo(() => {
    const result = validationResult?.data.alternative_configurations.find((item) => item.variant.id === option.productVariant.id && item.t_bolt?.diameter.name === option.diameter.name)

    if (!result) {
      return null
    }

    const value = utilisationMapping.shear.reduce((acc: number | null, item: string) => {
      const record = result?.validation_result.find((dataItem) => dataItem.id === item)

      const utilisation = record?.utilisation ? parseFloat(record?.utilisation) : null

      if (!utilisation) {
        return null
      }

      if (utilisation > (acc ?? 0)) {
        return utilisation
      } else {
        return acc
      }
    }, null)

    return value ? value * 100 : null
  }, [option, validationResult])

  const combinedPercentage = useMemo(() => {
    const result = validationResult?.data.alternative_configurations.find((item) => item.variant.id === option.productVariant.id && item.t_bolt?.diameter.name === option.diameter.name)

    if (!result) {
      return null
    }

    const value = utilisationMapping.combined.reduce((acc: number | null, item: string) => {
      const record = result?.validation_result.find((dataItem) => dataItem.id === item)

      const utilisation = record?.utilisation ? parseFloat(record?.utilisation) : null

      if (!utilisation) {
        return null
      }

      if (utilisation > (acc ?? 0)) {
        return utilisation
      } else {
        return acc
      }
    }, null)

    return value ? value * 100 : null
  }, [option, validationResult])

  const passOrFail = useMemo(() => {
    if (tensionPercentage === null || shearPercentage === null || combinedPercentage === null) {
      return null
    }

    return !(tensionPercentage >= 100 || shearPercentage >= 100 || combinedPercentage >= 100)
  }, [tensionPercentage, shearPercentage, combinedPercentage])

  return (
    <ProductConfigurationTable.Row onClick={() => dispatch(setSelectedProductConfigurationOption(option))}>
      <ProductConfigurationTable.Cell>
        <input
          className="w-4 h-4 accent-primary-900"
          type="radio"
          id={`${option.productVariant.name}-${option.diameter.name}`}
          name="tbolt-option"
          onChange={() => dispatch(setSelectedProductConfigurationOption(option))}
          checked={
            option.productVariant.id === selectedConfiguration?.productVariant.id
            && option.diameter.name === selectedConfiguration?.diameter.name
          }
        />
      </ProductConfigurationTable.Cell>
      <ProductConfigurationTable.Cell className="text-left">
        {option.productVariant.name}
      </ProductConfigurationTable.Cell>
      <ProductConfigurationTable.Cell className="text-right">
        {option.diameter.name}
      </ProductConfigurationTable.Cell>
      <ProductConfigurationTable.Cell className="text-right">
        {tensionPercentage ? `${tensionPercentage.toFixed(0)}%` : '-'}
      </ProductConfigurationTable.Cell>
      <ProductConfigurationTable.Cell className="text-right">
        {shearPercentage ? `${shearPercentage.toFixed(0)}%` : '-'}
      </ProductConfigurationTable.Cell>
      <ProductConfigurationTable.Cell className="text-right">
        {combinedPercentage ? `${combinedPercentage.toFixed(0)}%` : '-'}
      </ProductConfigurationTable.Cell>
      <ProductConfigurationTable.Cell className="text-right">
        {parent.cast_in_anchor_channel?.depth}mm
      </ProductConfigurationTable.Cell>
      <ProductConfigurationTable.Cell>
        <div className="flex flex-row justify-end pr-8">
          {passOrFail !== null ? (passOrFail ? <FilledCheckIcon /> : <FilledCrossIcon />) : '-'}
        </div>
      </ProductConfigurationTable.Cell>
    </ProductConfigurationTable.Row>
  )
}

const ProductConfiguration = ({ parent, options, validationResult }: PropTypes) => {
  return (
    <div className="w-full overflow-x-auto">
      <ProductConfigurationTable>
        <colgroup>
          <col style={{ width: '5%' }} /> {/* Radio button column */}
          <col style={{ width: '12%' }} /> {/* Bolt column */}
          <col style={{ width: '8%' }} /> {/* Bolt Diameter column */}
          <col style={{ width: '15%' }} /> {/* Tension evaluation column */}
          <col style={{ width: '15%' }} /> {/* Shear evaluation column */}
          <col style={{ width: '15%' }} /> {/* Combined evaluation column */}
          <col style={{ width: '20%' }} /> {/* Embedment depth column */}
          <col style={{ width: '10%' }} /> {/* Pass/Fail column */}
        </colgroup>
        <TBoltConfigurationHeader />
        <TBoltConfigurationBody
          parent={parent}
          options={options}
          validationResult={validationResult}
        />
      </ProductConfigurationTable>
    </div>
  )
}

export default ProductConfiguration
